/* Core & libraries */
import React, {useEffect, useState} from "react";
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

/* Helpers, constants, custom methods, internal libraries */
import {useGenericApiFetch} from "../endpoints/base";
import{ HOTEL_REPORTS_ENDPOINT, HOTEL_DOWNLOAD_REPORTS_ENDPOINT } from "../endpoints/endpoints";

import {useNotificationContext} from "../contexts/notificationContext";
import {useUserContext} from "../contexts/userContext";
import {filtersMap} from "helpers/filtersMap";
import csvDownload from 'json-to-csv-export';
import { currencyFormatWithLocale, dateFormatWithLocale, getHeadersForCsv } from '../helpers/general';
import { useLocaleContext } from '../contexts/localeContext';

/* Components */
/* Styles */
const messages = defineMessages({
  refundableTrue: {
    id: "complianceGrid.refundable.true",
  },
  refundableFalse: {
    id: "complianceGrid.refundable.false",
  },
})
export const useHotelReportsService = () => {
  const {genericApiFetch} = useGenericApiFetch();
  const {setNotification} = useNotificationContext();
  const [hotelReportsResponse, setHotelReportsResponse] = useState({});

  const userDetails = useUserContext();
  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode?.toLowerCase();
  const intl = useIntl();
  const locale = useLocaleContext();

  const getRowDescription = (gridValue) => {
    if (!gridValue) return "-";
    if (gridValue === "true" || gridValue.toLowerCase() === "yes")
      return intl.formatMessage(messages.refundableTrue);
    return intl.formatMessage(messages.refundableFalse);
  };

  const getHotelReportsList = async (options) => {
    if (options) {
      options = filtersMap(options);
      delete options["gridName"];
      const reportType = options?.reportType
      delete options["reportType"];
      const response = await genericApiFetch({
        url: HOTEL_REPORTS_ENDPOINT.replace("{reportType}", reportType).replace("{companyEntityId}", companyEntityId),
        method: "GET",
        queryParams: options,
        spinner: "hotelReportsSpinner"
      });
      if (response.success) {
        setHotelReportsResponse(response.data);
      } else {
        setHotelReportsResponse({ results: null });
      }
    }
  };

  const handleReportHotelDownload = async (filters) => {
    delete filters["page"];
    const reportType = filters?.reportType

    const formatCsvData = (items) => items?.map(row => {
      const formatted = { ...row };
      if (row.bookingDate) formatted.bookingDate = dateFormatWithLocale(row.bookingDate, locale);
      if (row.checkInDate) formatted.checkInDate = dateFormatWithLocale(row.checkInDate, locale);
      if (row.checkOutDate) formatted.checkOutDate = dateFormatWithLocale(row.checkOutDate, locale);
      if (row.estimatedTotalAfterTax) formatted.estimatedTotalAfterTax = currencyFormatWithLocale(row.estimatedTotalAfterTax, locale, row.currency);
      if (row.estimatedTotalBeforeTax) formatted.estimatedTotalBeforeTax = currencyFormatWithLocale(row.estimatedTotalBeforeTax, locale, row.currency);
      if (row.nightlyRate) formatted.nightlyRate = currencyFormatWithLocale(row.nightlyRate, locale, row.currency);
      if (row.cancellationDeadlineLocal) formatted.cancellationDeadlineLocal = dateFormatWithLocale(row.cancellationDeadlineLocal, locale);
      if (row.refundable) formatted.refundable = getRowDescription(row.refundable);
      if (row.inPolicy) formatted.inPolicy = getRowDescription(row.inPolicy);
      return formatted;
    });


    const response = await genericApiFetch({
      url: HOTEL_DOWNLOAD_REPORTS_ENDPOINT.replace("{providerCode}", providerCode
      ).replace("{reportType}", reportType
      ).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      queryParams: filters,
      spinner: "hotelReportDownloadSpinner",
    });
    if (response.success) {
      csvDownload({
        data: formatCsvData(response.data.items),
        filename: response.data?.fileName,
        delimiter: ",",
        fileType: "text/csv",
        headers: getHeadersForCsv(response.data.items, filters.gridName, intl)
      });
      return true
    }
    else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      )
      return false
    }
  };


  return {
    hotelReportsResponse,
    getHotelReportsList,
    handleReportHotelDownload
  };
}